import { useState, useCallback, useMemo, useEffect, useContext, useRef } from 'react'
import { Modal, Button } from 'antd'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getCurrentUser, signOut } from 'aws-amplify/auth'
import { AuthContext } from '@/contexts/AuthContext'
import type { Session } from '@/contexts/AuthContext'
import { NavigationProvider } from '@/contexts/NavigationContext'
import { useNavigation, usePermission, useIdleTimeout, useMaintenance } from '@/hooks'
import { authApi } from '@/services'
import { API_EVENTS, DASHBOARD_PERMISSIONS } from '@/constants'
import { DEMO_MENU_GROUPS } from '@/config/demoNavigation'
import { RequirePermission, SubdomainGuard } from '@/widgets/common'
import { clearLocalDevAuth, getLocalDevSession, isLocalDevLoginEnabled, saveSessionVersion } from '@/services/auth/localDev'
import { clearActivity, isIdleExpired } from '@/services/auth/sessionActivity'
import { setActingMunicipalityId } from '@/services/actingMunicipality'

import LoginScreen from '@/pages/auth/LoginScreen'
import AuthenticatorLoginScreen from '@/pages/auth/AuthenticatorLoginScreen'
import LogoutScreen from '@/pages/auth/LogoutScreen'
import type { AppDocDataLike } from '@/pages/application/ApplicationDocDetail'
import type { RecipientDataLike } from '@/pages/recipient/RecipientDetail'

const AuthLoginScreen = isLocalDevLoginEnabled()
  ? LoginScreen
  : import.meta.env.VITE_AUTH_UI === 'amplify'
  ? AuthenticatorLoginScreen
  : LoginScreen
import SubMenu from '@/widgets/layout/SubMenu'
import TopMenu from '@/pages/home/TopMenu'
import ApplicationRegistration from '@/pages/application/ApplicationRegistration'
import ApplicationReview from '@/pages/application/ApplicationReview'
import ApplicationDocDetail from '@/pages/application/ApplicationDocDetail'
import ApplicationDocEdit from '@/pages/application/ApplicationDocEdit'
import DataImport from '@/pages/data-import/DataImport'
import OcrReadImport from '@/pages/data-import/OcrReadImport'
import OcrReadList from '@/pages/data-import/OcrReadList'
import ImportErrorEdit from '@/pages/data-import/ImportErrorEdit'
import RecipientSearch from '@/pages/recipient/RecipientSearch'
import RecipientDetail from '@/pages/recipient/RecipientDetail'
import RecipientEdit from '@/pages/recipient/RecipientEdit'
import BankTransferExport from '@/pages/export/BankTransferExport'
import NotificationOutput from '@/pages/export/NotificationOutput'
import BusinessReport from '@/pages/report/BusinessReport'
import PaymentJobList from '@/pages/payment/PaymentJobList'
import PaymentJobCreate from '@/pages/payment/PaymentJobCreate'
import PaymentJobDetail from '@/pages/payment/PaymentJobDetail'
import UserManagement from '@/pages/user/UserManagement'
import OfficeReviewStatusMgmt from '@/pages/admin/OfficeReviewStatusMgmt'
import NoticeSettingsMgmt from '@/pages/admin/NoticeSettingsMgmt'
import TransferDatSettingsMgmt from '@/pages/admin/TransferDatSettingsMgmt'
import PrivacyPolicySettingsMgmt from '@/pages/admin/PrivacyPolicySettingsMgmt'
import RecipientCsvColumnSettings from '@/pages/admin/RecipientCsvColumnSettings'
import MfaSettings from '@/pages/settings/MfaSettings'
import UserEdit from '@/pages/user/UserEdit'
import UserCreate from '@/pages/user/UserCreate'
import LogOperationHistory from '@/pages/log/LogOperationHistory'
import MunicipalityManagement from '@/pages/municipality/MunicipalityManagement'
import MunicipalityEdit from '@/pages/municipality/MunicipalityEdit'
import MunicipalityCreate from '@/pages/municipality/MunicipalityCreate'
import MunicipalityOcrMapping from '@/pages/municipality/MunicipalityOcrMapping'
import MunicipalityMoushideshoOcrMapping from '@/pages/municipality/MunicipalityMoushideshoOcrMapping'
import ReleaseHistory from '@/pages/release/ReleaseHistory'
import OcrSuccessStats from '@/pages/admin/OcrSuccessStats'
import AnnouncementManagement from '@/pages/admin/AnnouncementManagement'
import NotFound from '@/pages/error/NotFound'
import Maintenance from '@/pages/error/Maintenance'

interface MenuItem {
  key: string
  icon: string
  title: string
  desc: string
}

function useMenuGroups() {
  const { t } = useTranslation('nav')
  const { hasPermission, hasAnyPermission } = usePermission()
  return useMemo(
    (): Record<string, { title: string; items: MenuItem[] }> =>
      Object.fromEntries(
        DEMO_MENU_GROUPS.map((group) => [
          group.id,
          {
            title: t(group.titleKey as never) as string,
            items: group.items
              .filter((item) => {
                if (item.requiredPermission && !hasPermission(item.requiredPermission)) return false
                if (item.requiredAnyPermission && item.requiredAnyPermission.length > 0
                    && !hasAnyPermission(...item.requiredAnyPermission)) return false
                return true
              })
              .map((item) => ({
                key: item.key,
                icon: item.icon,
                title: t(item.titleKey as never) as string,
                desc: t(item.descKey as never) as string,
              })),
          },
        ]),
      ),
    [t, hasPermission, hasAnyPermission],
  )
}

function SubMenuPage({ groupId }: { groupId: string }) {
  const menuGroups = useMenuGroups()
  const group = menuGroups[groupId]
  if (!group) return null
  return <SubMenu groupId={groupId} groupTitle={group.title} items={group.items} />
}

const emptyRecipient = {
  identId: '',
  householdNo: '',
  name: '',
  address: '',
  benefitStatus: '',
  comment: '',
  fields: {} as Record<string, string>,
}

const emptyAppDoc = {
  identId: '',
  id: '',
  applicationNo: '',
  name: '',
  applicationDate: '',
  status: '',
  householdNo: '',
  members: [] as unknown[],
  pastAddresses: [] as unknown[],
  bank: {} as Record<string, string>,
  otherHouseholdMembers: [] as unknown[],
  additionRows: [] as unknown[],
  attachments: {} as Record<string, unknown>,
  memo: '',
  protectionResidenceSame: 'same',
}

function AppRoutes() {
  const nav = useNavigation()
  const auth = useContext(AuthContext)
  const { t: tEnum } = useTranslation('enums')
  // UserEdit 用の表示名。session.roleDisplay が空の場合は role enum key から i18n で復元。
  const sessionViewerRole = auth?.session?.roleDisplay
    || (auth?.session?.role
      ? tEnum(`roles.${auth.session.role}`, { defaultValue: auth.session.role })
      : tEnum('roles.ADMIN'))
  const [recipientData, setRecipientData] = useState<RecipientDataLike>(() => ({ ...emptyRecipient }))
  const [appDocData, setAppDocData] = useState<AppDocDataLike>(() => ({ ...emptyAppDoc }))

  return (
    <>
      <Routes>
        <Route path="/" element={<TopMenu />} />
        <Route path="/menu/data-input" element={<Navigate to="/ocr-read-list" replace />} />
        <Route path="/menu/recipient-mgmt" element={<Navigate to="/recipient/search" replace />} />
        <Route path="/menu/payment" element={<Navigate to="/recipient/search" replace />} />
        <Route path="/menu/report" element={<SubMenuPage groupId="report" />} />
        <Route path="/menu/job" element={<Navigate to="/payment-job" replace />} />
        <Route path="/menu/admin" element={<SubMenuPage groupId="admin" />} />
        <Route path="/application/register" element={<RequirePermission permission="APPLICATION_CREATE_MANUAL"><ApplicationRegistration /></RequirePermission>} />
        <Route path="/application/review/:batchId?/:caseId?/:idx?" element={<RequirePermission permission="APPLICATION_REVIEW"><ApplicationReview initialCaseIndex={nav.ledgerReviewCaseIndex} importBatchId={nav.ledgerReviewImportBatchId} ocrCaseId={nav.ledgerReviewOcrCaseId} parentCrumb={nav.applicationReviewParentCrumb} pageTitle={nav.applicationReviewPageTitle} subjectIdentId={recipientData.identId || undefined} /></RequirePermission>} />
        <Route path="/application/doc-detail" element={<Navigate to="/recipient/search" replace />} />
        <Route path="/application/doc-edit" element={<Navigate to="/recipient/search" replace />} />
        <Route path="/application/documents" element={<RequirePermission permission="APPLICATION_VIEW"><ApplicationDocDetail data={appDocData} setData={setAppDocData} showNotification={nav.showNotification} /></RequirePermission>} />
        <Route path="/application/documents/edit" element={<RequirePermission permissions={['APPLICATION_EDIT_COMMENT', 'APPLICATION_EDIT_NON_COMMENT']}><ApplicationDocEdit data={appDocData} setData={setAppDocData} returnPage={nav.appDocEditReturnPage} /></RequirePermission>} />
        <Route path="/application/documents/new/:identId" element={<RequirePermission permission="APPLICATION_CREATE_MANUAL"><ApplicationDocEdit data={appDocData} setData={setAppDocData} returnPage={nav.appDocEditReturnPage} /></RequirePermission>} />
        <Route path="/application/documents/:applicationId" element={<RequirePermission permission="APPLICATION_VIEW"><ApplicationDocDetail data={appDocData} setData={setAppDocData} showNotification={nav.showNotification} /></RequirePermission>} />
        <Route path="/application/documents/:applicationId/edit" element={<RequirePermission permissions={['APPLICATION_EDIT_COMMENT', 'APPLICATION_EDIT_NON_COMMENT']}><ApplicationDocEdit data={appDocData} setData={setAppDocData} returnPage={nav.appDocEditReturnPage} /></RequirePermission>} />
        <Route path="/data-import" element={<RequirePermission permission="DATA_IMPORT_CSV"><DataImport /></RequirePermission>} />
        <Route path="/ocr-read-import" element={<RequirePermission permission="DATA_IMPORT_OCR" requireOcrUse><OcrReadImport /></RequirePermission>} />
        <Route path="/ocr-read-list" element={<RequirePermission permission="DATA_IMPORT_HISTORY_SEARCH"><OcrReadList /></RequirePermission>} />
        <Route path="/data-import/errors" element={<Navigate to="/ocr-read-list" replace />} />
        <Route path="/data-import/errors/:batchId" element={<RequirePermission permission="DATA_IMPORT_HISTORY_DOWNLOAD"><ImportErrorEdit /></RequirePermission>} />
        <Route path="/recipient/search" element={<RequirePermission permission="RECIPIENT_SEARCH"><RecipientSearch /></RequirePermission>} />
        <Route path="/recipient/detail" element={<Navigate to="/recipient/search" replace />} />
        <Route path="/recipient/detail/:recipientId" element={<RequirePermission permission="RECIPIENT_VIEW"><RecipientDetail data={recipientData} appDoc={appDocData} baseCrumb={nav.recipientBaseCrumb} detailPageTitle={nav.recipientDetailPageTitle} pendingAlert={nav.pendingAlert} clearPendingAlert={() => nav.setPendingAlert(null)} /></RequirePermission>} />
        <Route path="/recipient/detail/ident/:identId" element={<RequirePermission permission="RECIPIENT_VIEW"><RecipientDetail data={recipientData} appDoc={appDocData} baseCrumb={nav.recipientBaseCrumb} detailPageTitle={nav.recipientDetailPageTitle} pendingAlert={nav.pendingAlert} clearPendingAlert={() => nav.setPendingAlert(null)} /></RequirePermission>} />
        <Route path="/recipient/edit" element={<Navigate to="/recipient/search" replace />} />
        <Route path="/recipient/edit/:recipientId" element={<RequirePermission permissions={['RECIPIENT_EDIT_COMMENT', 'RECIPIENT_EDIT_NON_COMMENT']}><RecipientEdit data={recipientData} setData={setRecipientData} returnDetailPageTitle={nav.recipientDetailPageTitle} returnBaseCrumb={nav.recipientBaseCrumb} /></RequirePermission>} />
        <Route path="/recipient/new" element={<RequirePermission permission="DATA_IMPORT_CREATE_RECIPIENT"><RecipientEdit data={recipientData} setData={setRecipientData} returnDetailPageTitle={nav.recipientDetailPageTitle} returnBaseCrumb={nav.recipientBaseCrumb} mode="new" /></RequirePermission>} />
        <Route path="/export/bank-transfer" element={<RequirePermission permission="PAYMENT_EXPORT_ZENGIN"><BankTransferExport /></RequirePermission>} />
        <Route path="/export/notification" element={<RequirePermission permission="PAYMENT_NOTICE_DOWNLOAD"><NotificationOutput /></RequirePermission>} />
        <Route path="/report" element={<RequirePermission permissions={DASHBOARD_PERMISSIONS}><BusinessReport /></RequirePermission>} />
        <Route path="/payment-job" element={<RequirePermission permission="PAYMENT_LIST_SEARCH"><PaymentJobList /></RequirePermission>} />
        <Route path="/payment-job/create" element={<RequirePermission permission="PAYMENT_LIST_CREATE"><PaymentJobCreate /></RequirePermission>} />
        <Route path="/payment-job/create/:jobId" element={<RequirePermission permission="PAYMENT_LIST_EDIT"><PaymentJobCreate /></RequirePermission>} />
        <Route path="/payment-job/detail" element={<Navigate to="/payment-job" replace />} />
        <Route path="/payment-job/detail/:jobId" element={<RequirePermission permission="PAYMENT_LIST_SEARCH"><PaymentJobDetail /></RequirePermission>} />
        <Route path="/admin/users" element={<RequirePermission permission="USER_MGMT"><UserManagement /></RequirePermission>} />
        <Route path="/settings/mfa" element={<MfaSettings />} />
        <Route path="/admin/users/edit" element={<Navigate to="/admin/users" replace />} />
        <Route path="/admin/users/edit/:userId" element={<RequirePermission permission="USER_MGMT"><UserEdit viewerRole={sessionViewerRole} /></RequirePermission>} />
        <Route path="/admin/users/create" element={<RequirePermission permission="USER_MGMT"><UserCreate /></RequirePermission>} />
        <Route path="/admin/office-review-statuses" element={<RequirePermission permission="USER_MGMT"><OfficeReviewStatusMgmt /></RequirePermission>} />
        <Route path="/admin/notice-settings" element={<RequirePermission permission="USER_MGMT"><NoticeSettingsMgmt /></RequirePermission>} />
        <Route path="/admin/transfer-dat-settings" element={<RequirePermission permission="USER_MGMT"><TransferDatSettingsMgmt /></RequirePermission>} />
        <Route path="/admin/privacy-policy-settings" element={<RequirePermission permission="USER_MGMT"><PrivacyPolicySettingsMgmt /></RequirePermission>} />
        <Route path="/admin/recipient-csv-columns" element={<RequirePermission permission="USER_MGMT"><RecipientCsvColumnSettings /></RequirePermission>} />
        <Route path="/admin/log" element={<RequirePermission permission="OPERATION_LOG_VIEW"><LogOperationHistory /></RequirePermission>} />
        {/* リリース履歴（更新履歴）の閲覧は全ログインユーザーに開放。作成・編集ボタンは
            ページ内で RELEASE_HISTORY_VIEW を持つユーザー（SYSTEM_ADMIN）にのみ表示する。 */}
        <Route path="/admin/releases" element={<ReleaseHistory />} />
        <Route path="/admin/ocr-success-stats" element={<RequirePermission permission="OCR_SUCCESS_STATS_VIEW"><OcrSuccessStats /></RequirePermission>} />
        <Route path="/admin/municipalities" element={<RequirePermission permission="MUNICIPALITY_MGMT"><MunicipalityManagement /></RequirePermission>} />
        <Route path="/admin/municipalities/edit" element={<Navigate to="/admin/municipalities" replace />} />
        <Route path="/admin/municipalities/edit/:code" element={<RequirePermission permission="MUNICIPALITY_MGMT"><MunicipalityEdit /></RequirePermission>} />
        <Route path="/admin/municipalities/create" element={<RequirePermission permission="MUNICIPALITY_MGMT"><MunicipalityCreate /></RequirePermission>} />
        <Route path="/admin/municipalities/ocr-mapping" element={<Navigate to="/admin/municipalities" replace />} />
        <Route path="/admin/municipalities/ocr-mapping/:code" element={<RequirePermission permission="MUNICIPALITY_MGMT"><MunicipalityOcrMapping /></RequirePermission>} />
        <Route path="/admin/municipalities/moushidesho-ocr-mapping" element={<Navigate to="/admin/municipalities" replace />} />
        <Route path="/admin/municipalities/moushidesho-ocr-mapping/:code" element={<RequirePermission permission="MUNICIPALITY_MGMT"><MunicipalityMoushideshoOcrMapping /></RequirePermission>} />
        <Route path="/admin/announcements" element={<RequirePermission permission="HOME_SYSTEM_MGMT_MENU"><AnnouncementManagement /></RequirePermission>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  )
}

function App() {
  // 起動時に 50 分以上操作なしなら、保存済み localDev セッションは破棄して再ログインさせる。
  // Cognito モードは後段の useEffect で signOut → clearActivity するので、
  // ここでは clearActivity を呼ばない（呼ぶと useEffect 側の判定が false に戻ってしまう）。
  const initialSession = (() => {
    if (isIdleExpired()) {
      if (isLocalDevLoginEnabled()) {
        clearLocalDevAuth()
        clearActivity()
      }
      setActingMunicipalityId(null)
      return null
    }
    return getLocalDevSession()
  })()
  const [session, setSession] = useState<Session | null>(initialSession)
  const [loading, setLoading] = useState(() => !initialSession && !isLocalDevLoginEnabled())
  const [authView, setAuthView] = useState<'login' | 'logout'>('login')
  const [showIdleWarning, setShowIdleWarning] = useState(false)
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()

  // メンテナンスモード（顧客指摘#15）: ログイン前後どちらでも状態を監視する。
  const maintenance = useMaintenance()
  // メンテナンス操作・素通りを許す deploy 管理者判定（ログイン ID に "deploy" を含む）。
  const isDeployAdmin = !!session?.loginId && session.loginId.toLowerCase().includes('deploy')
  // 万一メンテ中に deploy 管理者がログアウトしても再ログインできるよう、?mbypass=1 で sorry 画面を回避できる。
  const maintenanceBypass = useMemo(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('mbypass'),
    [],
  )

  const login = useCallback((payload: Session) => {
    setSession(payload)
  }, [])

  const logout = useCallback(async () => {
    try { await authApi.logout() } catch { /* ignore */ }
    if (isLocalDevLoginEnabled()) {
      clearLocalDevAuth()
    }
    clearActivity()
    // 選択中の自治体（SYSTEM_ADMIN セレクター）は同一タブで別ユーザーに引き継がせない。
    setActingMunicipalityId(null)
    setSession(null)
    setAuthView('logout')
    navigate('/', { replace: true })
  }, [navigate])

  const handleSessionExpired = useCallback(() => {
    void authApi.logout().catch(() => undefined)
    clearActivity()
    setActingMunicipalityId(null)
    setSession(null)
    setAuthView('login')
    navigate('/', { replace: true })
  }, [navigate])

  const handleSessionInvalidated = useCallback(() => {
    void authApi.logout().catch(() => undefined)
    clearActivity()
    setSession(null)
    setAuthView('login')
    navigate('/', { replace: true })
    // 少し遅延させてから通知を表示（ログイン画面レンダリング後）
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent(API_EVENTS.API_ERROR, {
        detail: { message: 'このアカウントは別のデバイスでログインされたため、セッションが無効になりました。' },
      }))
    }, 500)
  }, [navigate])

  useEffect(() => {
    if (getLocalDevSession()) return
    if (isLocalDevLoginEnabled()) return

    let cancelled = false

    // 50 分超操作なしで再アクセスされた場合は、保存済み Cognito token を破棄してログイン画面に戻す。
    if (isIdleExpired()) {
      void signOut().catch(() => undefined).finally(() => {
        clearActivity()
        if (!cancelled) {
          setSession(null)
          setLoading(false)
        }
      })
      return () => { cancelled = true }
    }

    getCurrentUser()
      .then(() => authApi.me())
      .then(({ data }) => {
        if (!cancelled) {
          if (data.sessionVersion != null) {
            saveSessionVersion(data.sessionVersion)
          }
          login({
            cognitoSub: data.cognitoSub,
            municipality: data.municipality || '',
            municipalityId: data.municipalityId ?? null,
            municipalitySubdomain: data.municipalitySubdomain ?? null,
            ocrUse: data.ocrUse ?? false,
            loginId: data.loginId,
            displayName: data.displayName,
            role: data.role,
            roleDisplay: data.roleDisplay ?? '',
            permissions: data.permissions ?? [],
            sessionVersion: data.sessionVersion,
          })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSession(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [login])

  const warningShownAtRef = useRef<number>(0)
  const forceLogoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const WARNING_GRACE_MS = 5 * 60 * 1000

  const clearForceLogoutTimer = useCallback(() => {
    if (forceLogoutTimerRef.current) {
      clearTimeout(forceLogoutTimerRef.current)
      forceLogoutTimerRef.current = null
    }
  }, [])

  const handleIdleTimeout = useCallback(() => {
    clearForceLogoutTimer()
    setShowIdleWarning(false)
    void authApi.logout().catch(() => undefined)
    if (isLocalDevLoginEnabled()) clearLocalDevAuth()
    clearActivity()
    setSession(null)
    setAuthView('login')
    navigate('/', { replace: true })
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent(API_EVENTS.API_ERROR, {
        detail: { message: tc('idleTimeout.timeoutMessage') },
      }))
    }, 500)
  }, [tc, clearForceLogoutTimer, navigate])

  const handleWarn = useCallback(() => {
    warningShownAtRef.current = Date.now()
    clearForceLogoutTimer()
    forceLogoutTimerRef.current = setTimeout(() => {
      handleIdleTimeout()
    }, WARNING_GRACE_MS)
    setShowIdleWarning(true)
  }, [handleIdleTimeout, clearForceLogoutTimer, WARNING_GRACE_MS])

  const resetIdleTimer = useIdleTimeout({
    onWarn: handleWarn,
    onIdle: handleIdleTimeout,
    enabled: !!session && !showIdleWarning,
  })

  const handleContinueSession = useCallback(() => {
    if (Date.now() - warningShownAtRef.current >= WARNING_GRACE_MS) {
      handleIdleTimeout()
      return
    }
    clearForceLogoutTimer()
    setShowIdleWarning(false)
    resetIdleTimer()
  }, [resetIdleTimer, handleIdleTimeout, clearForceLogoutTimer, WARNING_GRACE_MS])

  useEffect(() => {
    const handler = () => { handleSessionExpired() }
    window.addEventListener(API_EVENTS.UNAUTHORIZED, handler)
    return () => window.removeEventListener(API_EVENTS.UNAUTHORIZED, handler)
  }, [handleSessionExpired])

  useEffect(() => {
    const handler = () => { handleSessionInvalidated() }
    window.addEventListener(API_EVENTS.SESSION_INVALIDATED, handler)
    return () => window.removeEventListener(API_EVENTS.SESSION_INVALIDATED, handler)
  }, [handleSessionInvalidated])

  // メンテナンス中は deploy 管理者以外のログイン中ユーザーを強制ログアウトする。
  // （バックエンドも session_version を bump 済みだが、フロント側セッションも破棄して再ログインを必須にする）
  useEffect(() => {
    if (!maintenance.enabled || !session || isDeployAdmin) return
    let cancelled = false
    void authApi.logout().catch(() => undefined).finally(() => {
      if (cancelled) return
      if (isLocalDevLoginEnabled()) clearLocalDevAuth()
      clearActivity()
      setSession(null)
      setAuthView('login')
    })
    return () => { cancelled = true }
  }, [maintenance.enabled, session, isDeployAdmin])

  const authContextValue = useMemo(() => ({ session, loading, login, logout }), [session, loading, login, logout])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-500 text-sm">{tc('loading')}</div>
      </div>
    )
  }

  // メンテナンス中は sorry 画面を最優先で表示する（ログイン不可＋強制ログアウト後の表示）。
  // deploy 管理者はメンテ操作のため通常画面を表示。?mbypass=1 で deploy 管理者の再ログイン経路を確保。
  if (maintenance.enabled && !isDeployAdmin && !maintenanceBypass) {
    return <Maintenance message={maintenance.message} />
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      {!session ? (
        authView === 'logout' ? (
          <LogoutScreen onNavigateToLogin={() => setAuthView('login')} />
        ) : (
          <AuthLoginScreen />
        )
      ) : (
        <NavigationProvider>
          <SubdomainGuard>
            <AppRoutes />
          </SubdomainGuard>
        </NavigationProvider>
      )}
      <Modal
        open={showIdleWarning}
        title={tc('idleTimeout.warningTitle')}
        closable={false}
        maskClosable={false}
        footer={[
          <Button key="continue" type="primary" onClick={handleContinueSession}>
            {tc('idleTimeout.continueButton')}
          </Button>,
          <Button key="logout" onClick={handleIdleTimeout}>
            {tc('idleTimeout.logoutButton')}
          </Button>,
        ]}
      >
        <p>{tc('idleTimeout.warningMessage')}</p>
      </Modal>
    </AuthContext.Provider>
  )
}

export default App
